import { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'
import { InvitationChannelPricingType } from '@app/v2/event/types/invitation-channel-pricing.type'
import { InvitationChannelBlockedSendReason } from '@app/v2/event/utils/invitation-channel-blocked-send.util'
import {
  INVITATION_CHANNEL_TO_API,
  InvitationChannel,
  invitationHistoryStatusTone,
  InvitationHistoryStatusTone
} from '@app/v2/event/utils/invitation-channel-status.util'

export type InvitationChannelSendRequest = {
  collectionItemId: number
  channel: InvitationChannel
}

export { INVITATION_CHANNEL_TO_API, invitationHistoryStatusTone }
export type { InvitationHistoryStatusTone }

export const INVITATION_CHANNEL_TITLE: Record<InvitationChannel, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  sms: 'SMS'
}

export const INVITATION_CHANNEL_TO_NOTIFICATION: Record<InvitationChannel, NotificationChannel> = {
  email: NotificationChannel.EMAIL,
  whatsapp: NotificationChannel.WHATSAPP,
  sms: NotificationChannel.SMS
}

/** Dark Tippy popover header icons. */
export const INVITATION_CHANNEL_POPOVER_HEADER_ICON_SRC: Record<InvitationChannel, string> = {
  email: 'assets/images/v2/invitation-channel-popover/envelope-header.svg',
  whatsapp: 'assets/images/v2/invitation-channel-popover/whatsapp.svg',
  sms: 'assets/images/v2/delivery-status/sms.svg'
}

/** Dark Tippy popover empty-state icons. */
export const INVITATION_CHANNEL_POPOVER_EMPTY_ICON_SRC: Record<InvitationChannel, string> = {
  email: 'assets/images/v2/invitation-channel-popover/envelope-empty.svg',
  whatsapp: 'assets/images/v2/delivery-status/whatsapp.svg',
  sms: 'assets/images/v2/delivery-status/sms.svg'
}

/** Light mobile modal header icons. */
export const INVITATION_CHANNEL_MOBILE_HEADER_ICON_SRC: Record<InvitationChannel, string> = {
  email: 'assets/images/v2/invitation-channel-popover/envelope-header-light.svg',
  whatsapp: 'assets/images/v2/delivery-status/whatsapp.svg',
  sms: 'assets/images/v2/delivery-status/sms.svg'
}

/** Light mobile modal empty-state icons. */
export const INVITATION_CHANNEL_MOBILE_EMPTY_ICON_SRC: Record<InvitationChannel, string> = {
  email: 'assets/images/v2/invitation-channel-popover/envelope-empty-light.svg',
  whatsapp: 'assets/images/v2/delivery-status/whatsapp.svg',
  sms: 'assets/images/v2/delivery-status/sms.svg'
}

const HISTORY_STATUS_BADGE_CLASS_LIGHT: Record<InvitationHistoryStatusTone, string> = {
  delivered: 'tw-bg-green-100 tw-text-green-800',
  failed: 'tw-bg-red-100 tw-text-red-800',
  pending: 'tw-bg-yellow-100 tw-text-yellow-800',
  unknown: 'tw-bg-gray-100 tw-text-gray-800'
}

const HISTORY_STATUS_BADGE_CLASS_DARK: Record<InvitationHistoryStatusTone, string> = {
  delivered: 'tw-bg-green-900 tw-text-green-300',
  failed: 'tw-bg-red-900 tw-text-red-300',
  pending: 'tw-bg-yellow-900 tw-text-yellow-300',
  unknown: 'tw-bg-gray-700 tw-text-gray-300'
}

const HISTORY_STATUS_DOT_CLASS: Record<InvitationHistoryStatusTone, string> = {
  delivered: 'tw-bg-green-500',
  failed: 'tw-bg-red-600',
  pending: 'tw-bg-yellow-400',
  unknown: 'tw-bg-gray-400'
}

const BLOCKED_SEND_BANNER_CLASS_DARK: Record<InvitationChannelBlockedSendReason, string> = {
  event_ended: 'tw-border-red-600 tw-bg-gray-800',
  registered: 'tw-border-green-500 tw-bg-gray-800',
  declined: 'tw-border-red-600 tw-bg-gray-800',
  unreachable: 'tw-border-red-600 tw-bg-gray-800',
  opt_out: 'tw-border-yellow-600 tw-bg-gray-800',
  channel_failed: 'tw-border-yellow-500 tw-bg-gray-800',
  channel_bounced: 'tw-border-yellow-500 tw-bg-gray-800'
}

const BLOCKED_SEND_BANNER_CLASS_LIGHT: Record<InvitationChannelBlockedSendReason, string> = {
  event_ended: 'tw-border-red-300 tw-bg-red-50',
  registered: 'tw-border-green-300 tw-bg-green-50',
  declined: 'tw-border-red-300 tw-bg-red-50',
  unreachable: 'tw-border-red-300 tw-bg-red-50',
  opt_out: 'tw-border-yellow-300 tw-bg-yellow-50',
  channel_failed: 'tw-border-yellow-300 tw-bg-yellow-50',
  channel_bounced: 'tw-border-yellow-300 tw-bg-yellow-50'
}

/**
 * Builds the channel invitations title, e.g. "Email invitations (0)".
 */
export function invitationChannelTitle(channel: InvitationChannel, count: number): string {
  return `${INVITATION_CHANNEL_TITLE[channel]} invitations (${count})`
}

/**
 * Builds the empty-state copy for a channel.
 */
export function invitationChannelEmptyCopy(channel: InvitationChannel): string {
  return `No ${INVITATION_CHANNEL_TITLE[channel]} invitations sent so far.`
}

/**
 * Resolves the unit price for a channel CTA from AuthService price maps.
 * @param channel - The channel to resolve the unit price for.
 * @param pricingKind - The pricing kind to resolve the unit price for.
 * @param invitationUnitPriceUsd - The invitation unit price map.
 * @param resendConfirmationUnitPriceUsd - The resend confirmation unit price map.
 * @returns The unit price for the channel.
 */
export function invitationChannelUnitPrice(
  channel: InvitationChannel,
  pricingKind: InvitationChannelPricingType,
  invitationUnitPriceUsd: Readonly<Record<NotificationChannel, number>>,
  resendConfirmationUnitPriceUsd: Readonly<Record<NotificationChannel, number>>
): number {
  const notificationChannel = INVITATION_CHANNEL_TO_NOTIFICATION[channel]
  return pricingKind === 'resend' ? resendConfirmationUnitPriceUsd[notificationChannel] : invitationUnitPriceUsd[notificationChannel]
}

/**
 * Formats a USD amount as `$X,XXX.XX`.
 * @param amount {number} - The amount to format; non-numeric values render as `$0.00`.
 */
export function formatUsdAmount(amount: number): string {
  return `$${(Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Formats a channel unit price as `$X.XX`.
 */
export function invitationChannelUnitPriceLabel(price: number): string {
  return formatUsdAmount(price)
}

/**
 * Builds the send CTA label, e.g. "Send invitation ($0.10)".
 */
export function invitationChannelSendCtaLabel(unitPriceLabel: string): string {
  return `Send invitation (${unitPriceLabel})`
}

/**
 * Stable track key for a send-invitation history row (API returns sorted data, no row id).
 */
export function invitationHistoryEntryTrackKey(entry: { status: string; by: string; created_at: string }, index: number): string {
  return `${entry.created_at}|${entry.by}|${entry.status}|${index}`
}

/**
 * Whether a scrollable history list is near the bottom and should load the next page.
 */
export function shouldLoadMoreInvitationHistoryOnScroll(event: Event, thresholdPx = 24): boolean {
  const el = event.target as HTMLElement | null
  if (!el) return false
  return el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx
}

/**
 * Light-surface status badge classes (mobile modal).
 * @param status {string} - The invitation status to get the badge class for.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function invitationHistoryStatusBadgeClass(status: string, channel: InvitationChannel): string {
  return HISTORY_STATUS_BADGE_CLASS_LIGHT[invitationHistoryStatusTone(status, channel)]
}

/**
 * Dark-surface status badge classes (desktop Tippy).
 * @param status {string} - The invitation status to get the badge class for.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function invitationHistoryStatusBadgeClassDark(status: string, channel: InvitationChannel): string {
  return HISTORY_STATUS_BADGE_CLASS_DARK[invitationHistoryStatusTone(status, channel)]
}

/**
 * Status dot classes for history rows.
 * @param status {string} - The invitation status to get the dot class for.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function invitationHistoryStatusDotClass(status: string, channel: InvitationChannel): string {
  return HISTORY_STATUS_DOT_CLASS[invitationHistoryStatusTone(status, channel)]
}

/**
 * Formats cumulative send cost as `TC $X.XX`.
 * @param totalCost {number} - The total cost to format.
 */
export function invitationHistoryTotalCostLabel(totalCost: number): string {
  return `TC: ${formatUsdAmount(totalCost)}`
}

/** Dark Tippy blocked-send footer icon by reason. Event ended / bounced reuse the failed warning SVG; Unreachable / Opt out reuse declined. */
export const INVITATION_CHANNEL_BLOCKED_SEND_ICON_SRC: Record<InvitationChannelBlockedSendReason, string> = {
  event_ended: 'assets/images/v2/invitation-channel-popover/declined-exclamation.svg',
  registered: 'assets/images/v2/invitation-channel-popover/registered-check.svg',
  declined: 'assets/images/v2/invitation-channel-popover/declined-exclamation.svg',
  unreachable: 'assets/images/v2/invitation-channel-popover/declined-exclamation.svg',
  opt_out: 'assets/images/v2/invitation-channel-popover/failed-warning.svg',
  channel_failed: 'assets/images/v2/invitation-channel-popover/failed-warning.svg',
  channel_bounced: 'assets/images/v2/invitation-channel-popover/failed-warning.svg'
}
/**
 * Dark Tippy blocked-send footer container classes by reason.
 * @param reason - Resolved block reason.
 */
export function invitationChannelBlockedSendBannerClassDark(reason: InvitationChannelBlockedSendReason): string {
  return BLOCKED_SEND_BANNER_CLASS_DARK[reason]
}

/**
 * Light mobile modal blocked-send footer container classes by reason.
 * @param reason - Resolved block reason.
 */
export function invitationChannelBlockedSendBannerClassLight(reason: InvitationChannelBlockedSendReason): string {
  return BLOCKED_SEND_BANNER_CLASS_LIGHT[reason]
}
